import { useState } from 'react';
import { useI18n } from '../../lib/i18n';
import Modal from '../ui/Modal';
import { Button } from '../ui/Button';
import { PillTabs } from '../ui/PillTabs';
import {
  FacebookIcon,
  ExternalLinkIcon,
  SmartphoneIcon,
  CloudIcon,
} from '../icons';
import {
  isEmbeddedSignupConfigured,
  launchEmbeddedSignup,
  exchangeEmbeddedSignup,
  type ExchangeResult,
  type SignupMode,
} from '../../lib/embeddedSignup';
import { saveIntegration } from '../../lib/whatsappApi';

export interface ConnectModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (integrationId?: string) => Promise<void>;
  prefilledWabaId?: string;
}

export function ConnectModal({
  open,
  onClose,
  onSuccess,
  prefilledWabaId,
}: ConnectModalProps) {
  const { t } = useI18n();
  const [tab, setTab] = useState<'embedded' | 'manual'>('embedded');

  // Embedded Signup state
  const [esBusy, setEsBusy] = useState(false);
  const [esMode, setEsMode] = useState<SignupMode | null>(null);
  const [esErr, setEsErr] = useState<string | null>(null);
  const [esSuccess, setEsSuccess] = useState<ExchangeResult | null>(null);

  // Manual setup state
  const [phoneId, setPhoneId] = useState('');
  const [wabaId, setWabaId] = useState(prefilledWabaId || '');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  async function handleConnectWithFacebook(mode: SignupMode) {
    if (!isEmbeddedSignupConfigured()) {
      setEsErr(t('connect.es.notConfigured'));
      return;
    }
    setEsBusy(true);
    setEsMode(mode);
    setEsErr(null);
    setEsSuccess(null);
    try {
      const result = await launchEmbeddedSignup(mode);
      const exchanged = await exchangeEmbeddedSignup(result);
      setEsSuccess(exchanged);
      await onSuccess(exchanged.integrationId);
    } catch (e) {
      setEsErr((e as Error).message);
    } finally {
      setEsBusy(false);
      setEsMode(null);
    }
  }

  async function handleSaveManual() {
    setSaving(true);
    setSaveErr(null);
    try {
      const saved = await saveIntegration({
        phoneNumberId: phoneId.trim(),
        businessAccountId: wabaId.trim(),
        token: token.trim() || undefined,
      });
      setToken('');
      await onSuccess(saved.id);
      onClose();
    } catch (e) {
      setSaveErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('connect.connectNew')}
    >
      <div className="space-y-4">
        <PillTabs
          tabs={[
            { id: 'embedded', label: t('connect.es.title') },
            { id: 'manual', label: t('connect.es.advanced') },
          ]}
          activeId={tab}
          onChange={(id) => setTab(id as 'embedded' | 'manual')}
        />

        {tab === 'embedded' ? (
          <div className="space-y-4 pt-1">
            <p className="text-xs text-ink-3 dark:text-slate-400">
              {t('connect.es.body')}
            </p>

            {esErr && (
              <div className="rounded-[9px] bg-danger-50 px-3 py-2 text-xs font-semibold text-danger dark:bg-rose-950/40 dark:text-rose-300">
                {esErr}
              </div>
            )}

            {esSuccess ? (
              <div className="rounded-card border border-success/30 bg-success-50/50 p-4 dark:border-emerald-800/40 dark:bg-emerald-950/20">
                <p className="text-sm font-bold text-success dark:text-emerald-400">
                  ✓ {esSuccess.coexistence ? t('connect.es.successCoex') : t('connect.es.success')}
                </p>
                <dl className="mt-2 space-y-1 text-xs text-ink-2 dark:text-slate-300">
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-3 dark:text-slate-400">{t('connect.field.phoneId')}</dt>
                    <dd className="font-mono text-ink dark:text-slate-200">{esSuccess.phoneNumberId}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-3 dark:text-slate-400">{t('connect.field.wabaId')}</dt>
                    <dd className="font-mono text-ink dark:text-slate-200">{esSuccess.businessAccountId}</dd>
                  </div>
                  {esSuccess.verifiedPhoneE164 && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-ink-3 dark:text-slate-400">{t('connect.health.number')}</dt>
                      <dd className="font-mono text-ink dark:text-slate-200" dir="ltr">{esSuccess.verifiedPhoneE164}</dd>
                    </div>
                  )}
                </dl>

                <ul className="mt-3 space-y-1 text-xs">
                  <li className={esSuccess.registered ? 'text-success font-semibold' : 'text-danger'}>
                    {esSuccess.registered ? '✓' : '✗'} {t('connect.es.step.register')}
                    {esSuccess.registerError && <span className="ms-1 text-ink-3 dark:text-slate-400">— {esSuccess.registerError}</span>}
                  </li>
                  {esSuccess.coexistence && (
                    <>
                      <li className={esSuccess.contactSyncRequested ? 'text-success font-semibold' : 'text-warning'}>
                        {esSuccess.contactSyncRequested ? '✓' : '⚠'} {t('connect.es.step.contacts')}
                      </li>
                      <li className={esSuccess.historySyncRequested ? 'text-success font-semibold' : 'text-warning'}>
                        {esSuccess.historySyncRequested ? '✓' : '⚠'} {t('connect.es.step.history')}
                      </li>
                      {esSuccess.syncError && <li className="text-ink-3 dark:text-slate-400">— {esSuccess.syncError}</li>}
                    </>
                  )}
                </ul>

                {esSuccess.coexistence && (
                  <div className="mt-3 rounded-[9px] bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:bg-sky-950/40 dark:text-sky-300">
                    <b>📱 {t('connect.es.coex.nextTitle')}</b>
                    <p className="mt-0.5">{t('connect.es.coex.nextBody')}</p>
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <Button size="sm" onClick={onClose}>
                    {t('common.done')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Option A — Coexistence */}
                <div className="flex flex-col justify-between rounded-card border border-line bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand-50 text-brand dark:bg-brand-950/40 dark:text-brand-tint">
                        <SmartphoneIcon size={16} />
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-brand dark:text-brand-tint">
                        {t('connect.es.coex.badge')}
                      </span>
                    </div>
                    <h3 className="mt-2 text-sm font-extrabold text-ink dark:text-slate-100">
                      {t('connect.es.coex.title')}
                    </h3>
                    <p className="mt-1 text-xs text-ink-3 dark:text-slate-400">
                      {t('connect.es.coex.body')}
                    </p>
                    <ul className="mt-2 space-y-1 text-[11px] text-ink-2 dark:text-slate-300">
                      <li>• {t('connect.es.coex.req1')}</li>
                      <li>• {t('connect.es.coex.req2')}</li>
                      <li>• {t('connect.es.coex.req3')}</li>
                    </ul>
                  </div>

                  <Button
                    type="button"
                    onClick={() => void handleConnectWithFacebook('business_app')}
                    disabled={esBusy}
                    className="mt-4 w-full bg-facebook text-white hover:bg-facebook/90"
                  >
                    <FacebookIcon size={16} className="me-1.5" />
                    {esBusy && esMode === 'business_app' ? t('connect.es.connecting') : t('connect.es.coex.button')}
                  </Button>
                </div>

                {/* Option B — Cloud API */}
                <div className="flex flex-col justify-between rounded-card border border-line bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="grid h-7 w-7 place-items-center rounded-lg bg-surface text-ink-2 dark:bg-slate-800 dark:text-slate-300">
                        <CloudIcon size={16} />
                      </span>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-slate-400">
                        {t('connect.es.cloud.badge')}
                      </span>
                    </div>
                    <h3 className="mt-2 text-sm font-extrabold text-ink dark:text-slate-100">
                      {t('connect.es.cloud.title')}
                    </h3>
                    <p className="mt-1 text-xs text-ink-3 dark:text-slate-400">
                      {t('connect.es.cloud.body')}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void handleConnectWithFacebook('cloud_api')}
                    disabled={esBusy}
                    className="mt-4 w-full border-facebook text-facebook hover:bg-blue-50 dark:hover:bg-blue-950/30"
                  >
                    <FacebookIcon size={16} className="me-1.5" />
                    {esBusy && esMode === 'cloud_api' ? t('connect.es.connecting') : t('connect.es.cloud.button')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Manual setup tab */
          <div className="space-y-4 pt-1">
            <div className="rounded-card border border-line bg-surface/30 p-3.5 text-xs text-ink-2 dark:border-slate-800 dark:bg-slate-800/30 dark:text-slate-300">
              <div className="flex items-center justify-between">
                <span>{t('connect.step1.title')}</span>
                <a
                  href="https://developers.facebook.com/apps"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-brand hover:underline"
                >
                  {t('connect.step1.cta')}
                  <ExternalLinkIcon size={12} />
                </a>
              </div>
              <p className="mt-1 text-ink-3 dark:text-slate-400">{t('connect.step1.body')}</p>
            </div>

            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.field.phoneId')}
                </span>
                <input
                  value={phoneId}
                  onChange={(e) => setPhoneId(e.target.value)}
                  placeholder={t('connect.phoneIdPlaceholder') || 'e.g. 104829104829104'}
                  className="w-full rounded-[9px] border border-line bg-white px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.field.wabaId')}
                </span>
                <input
                  value={wabaId}
                  onChange={(e) => setWabaId(e.target.value)}
                  placeholder={t('connect.wabaIdPlaceholder') || 'e.g. 293810293810293'}
                  className="w-full rounded-[9px] border border-line bg-white px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-ink-2 dark:text-slate-300">
                  {t('connect.field.token')}
                </span>
                <div className="flex gap-2">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="EAAG..."
                    className="min-w-0 flex-1 rounded-[9px] border border-line bg-white px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? t('connect.token.hide') : t('connect.token.show')}
                  </Button>
                </div>
              </label>

              {saveErr && (
                <div className="rounded-[9px] bg-danger-50 px-3 py-2 text-xs font-semibold text-danger dark:bg-rose-950/40 dark:text-rose-300">
                  {saveErr}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" size="sm" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveManual}
                  disabled={saving || !phoneId.trim() || !wabaId.trim()}
                >
                  {saving ? t('connect.saving') : t('connect.save')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ConnectModal;
